import { z } from "zod";

export const paymentSchema = z
  .object({
    // Account
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Enter a valid email"),
    company: z.string().min(1, "Company is required"),
    phoneNumber: z
      .string()
      .min(1, "Phone number is required")
      .regex(/^\(\d{3}\) \d{3}-\d{4}$/, "Format: (XXX) XXX-XXXX"),

    // Billing address
    street: z.string().min(1, "Street address is required"),
    city: z.string().min(1, "City is required"),
    state: z.string().regex(/^[A-Za-z]{2}$/, "Use 2-letter state code (e.g. CA, TX)"),
    zip: z.string().regex(/^\d{5}(-\d{4})?$/, "Enter a valid ZIP code"),

    // Credit card
    cardNumber: z
      .string()
      .min(1, "Card number is required")
      .transform((v) => v.replace(/\s+/g, ""))
      .pipe(z.string().regex(/^\d{13,19}$/, "Enter a valid card number")),
    expirationMonth: z
      .string()
      .min(1, "Required")
      .regex(/^(0[1-9]|1[0-2])$/, "Valid month 01\u201312"),
    expirationYear: z
      .string()
      .min(1, "Required")
      .regex(/^\d{4}$/, "Use YYYY format")
      .refine(
        (val) => parseInt(val, 10) >= new Date().getFullYear(),
        "Card is expired",
      ),
    cvv: z
      .string()
      .min(1, "CVV is required")
      .regex(/^\d{3,4}$/, "Enter a valid CVV"),
    cardholderName: z.string().min(1, "Cardholder name is required"),

    // Login credentials — rules must match SignupService.ValidateUserName,
    // which mirrors the Tcl account manager (min 3 chars, a few illegal
    // characters, reserved admin names). Usernames are otherwise free-form.
    username: z
      .string()
      .trim()
      .min(3, "Username must be at least 3 characters")
      .regex(/^[^":/\\*?<>|\n]+$/, 'Username may not contain " : / \\ * ? < > |')
      .refine(
        (u) =>
          !/^(admin|subadmin|webadmin|administrator|supervisor|agent)$/i.test(u) &&
          !/sysadmin/i.test(u),
        "That username is reserved",
      ),
    password: z
      .string()
      .min(8, "At least 8 characters")
      .refine((val) => /[A-Z]/.test(val), "Must contain an uppercase letter")
      .refine((val) => /[a-z]/.test(val), "Must contain a lowercase letter")
      .refine((val) => /[0-9]/.test(val), "Must contain a number")
      .refine((val) => /[^a-zA-Z0-9\s]/.test(val), "Must contain a special character"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  })
  .refine(
    (d) => {
      const now = new Date();
      const month = parseInt(d.expirationMonth, 10);
      const year = parseInt(d.expirationYear, 10);
      if (isNaN(month) || isNaN(year)) return true;
      return year > now.getFullYear() || (year === now.getFullYear() && month >= now.getMonth() + 1);
    },
    {
      message: "Card is expired",
      path: ["expirationMonth"],
    },
  );

export type PaymentForm = z.infer<typeof paymentSchema>;

/** Individual group checks for the password rules live checklist. */
export const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (v: string) => v.length >= 8 },
  { label: "Contains uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
  { label: "Contains lowercase letter", test: (v: string) => /[a-z]/.test(v) },
  { label: "Contains number", test: (v: string) => /[0-9]/.test(v) },
  { label: "Contains special character", test: (v: string) => /[^a-zA-Z0-9\s]/.test(v) },
] as const;
